import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ThemeToggle } from '@/shared/components/theme-toggle'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { useSidebar } from '@/shared/ui/sidebar'

/**
 * The top row of a page: the sidebar's expand arrow while it is collapsed, the
 * page's own controls, and the theme toggle.
 *
 * Painted as the sidebar's surface with the same hairline ring, so the two read
 * as one frame around the page.
 */
export function PageBar({
  label,
  className,
  children,
}: {
  /** What this row is, for assistive technology. */
  label?: string
  className?: string
  children?: React.ReactNode
}) {
  return (
    <div
      aria-label={label}
      className={cn(
        'relative z-20 flex h-14 min-w-0 shrink-0 items-center gap-2 bg-sidebar px-2 shadow-sm ring-1 ring-foreground/10',
        className,
      )}
    >
      <SidebarExpandButton />
      {children}
      <div className="ml-auto shrink-0">
        <ThemeToggle />
      </div>
    </div>
  )
}

/**
 * Opens the sidebar. Shown only while it is collapsed, and always on a phone,
 * where the sidebar is a sheet with nothing on screen to open it from.
 */
function SidebarExpandButton() {
  const { t } = useTranslation()
  const { toggleSidebar, open, isMobile } = useSidebar()

  if (open && !isMobile) return null

  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={t('action.expandSidebar')}
      onClick={toggleSidebar}
      className="shrink-0 text-muted-foreground"
    >
      <ChevronRight />
    </Button>
  )
}
